import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSearchEventsMutation } from "@app/data/events/eventsApi"
import { useAppSelector } from "@app/hooks"
import { selectCurrentLocation } from "@shared/location/selectors"
import styles from "./EventPickerDialog.module.css"

// Города
const CITIES = [
  { id: "moscow", label: "Москва", tz: "Europe/Moscow", emoji: "🏙", lat: 55.7558, lng: 37.6173 },
  { id: "spb", label: "Санкт-Петербург", tz: "Europe/Moscow", emoji: "🌆", lat: 59.9311, lng: 30.3609 },
  { id: "kazan", label: "Казань", tz: "Europe/Moscow", emoji: "🕌", lat: 55.7887, lng: 49.1221 },
  { id: "ekb", label: "Екатеринбург", tz: "Asia/Yekaterinburg", emoji: "🏔", lat: 56.8389, lng: 60.6057 },
  { id: "nsk", label: "Новосибирск", tz: "Asia/Novosibirsk", emoji: "❄️", lat: 54.9885, lng: 82.9207 },
  { id: "other", label: "Другой", tz: "Europe/Moscow", emoji: "📍", lat: undefined as number | undefined, lng: undefined as number | undefined },
]

// Когда
const WHEN_OPTIONS = [
  { id: "today", label: "Сегодня", emoji: "⚡", days: 0 },
  { id: "tomorrow", label: "Завтра", emoji: "📅", days: 1 },
  { id: "weekend", label: "В выходные", emoji: "🎉", days: 6 },
  { id: "week", label: "На неделе", emoji: "📆", days: 7 },
]

// Категории (id из базы Tribe)
const FORMAT_OPTIONS = [
  { id: 17, label: "Игры", emoji: "🎲" },
  { id: 17, label: "Трансформационные", emoji: "✨" },
  { id: 17, label: "Мафия", emoji: "🕵️" },
  { id: 17, label: "Квиз", emoji: "🧠" },
  { id: 1, label: "Спорт", emoji: "⚽" },
  { id: 4, label: "Вечеринки", emoji: "🎊" },
  { id: 5, label: "Обучение", emoji: "📚" },
  { id: 15, label: "Знакомства", emoji: "💫" },
]

// Теги интересов
const INTEREST_TAGS = [
  "психология", "саморазвитие", "знакомства", "медитация",
  "настолки", "творчество", "танцы", "йога", "кино", "музыка",
  "природа", "еда", "арт", "коучинг", "мафия", "квиз",
]

type Step = "city" | "when" | "format" | "tags" | "result"

interface Selection {
  city?: typeof CITIES[0]
  when?: typeof WHEN_OPTIONS[0]
  format?: typeof FORMAT_OPTIONS[0]
  tags: string[]
}

interface FoundEvent {
  eventId: number
  eventName: string
  city?: string
  startTime?: string
  avatarUrl?: string[]
  organizerUsername?: string
  description?: string
}

// Получить дату для фильтра
function getDateRange(days: number) {
  const now = new Date()
  const start = new Date(now)
  start.setHours(0, 0, 0, 0)
  const end = new Date(start)
  if (days === 0) {
    end.setHours(23, 59, 59, 999)
  } else {
    end.setDate(end.getDate() + days)
    end.setHours(23, 59, 59, 999)
  }
  // Бэкенд ждёт toLocaleDateString() — тот же формат что использует FilterDateRange ("26.09.2026")
  return {
    startDate: start.toLocaleDateString(),
    endDate: end.toLocaleDateString(),
  }
}

// Поиск событий через Tribe API


function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })
  } catch { return iso }
}

export function EventPickerDialog() {
  const navigate = useNavigate()
  const [searchEventsMutation] = useSearchEventsMutation()
  const currentLocation = useAppSelector(selectCurrentLocation)

  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<Step>("city")
  // Москва выбрана по умолчанию — соответствует ЦА и дефолту сайта
  const defaultCity = CITIES.find(c => c.id === "moscow")!
  const [selection, setSelection] = useState<Selection>({ tags: [], city: defaultCity })
  const [events, setEvents] = useState<FoundEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [otherCity, setOtherCity] = useState("")

  const reset = () => { setStep("city"); setSelection({ tags: [] }); setEvents([]); setError(null) }
  const close = () => { setOpen(false); reset() }

  const pickCity = (city: typeof CITIES[0]) => {
    setSelection(s => ({ ...s, city }))
    setStep("when")
  }

  const pickWhen = (when: typeof WHEN_OPTIONS[0]) => {
    setSelection(s => ({ ...s, when }))
    setStep("format")
  }

  const pickFormat = (format: typeof FORMAT_OPTIONS[0]) => {
    setSelection(s => ({ ...s, format }))
    setStep("tags")
  }

  const toggleTag = (tag: string) => {
    setSelection(s => ({
      ...s,
      tags: s.tags.includes(tag) ? s.tags.filter(t => t !== tag) : [...s.tags, tag],
    }))
  }

  const doSearch = async () => {
    setLoading(true)
    setError(null)
    setStep("result")
    try {
      const { startDate, endDate } = getDateRange(selection.when?.days ?? 7)
      const tz = selection.city?.tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone
      const cityLat = selection.city?.lat ?? currentLocation?.latitude
      const cityLng = selection.city?.lng ?? currentLocation?.longitude
      const data = await searchEventsMutation({
        size: 20,
        page: 0,
        filter: {
          zoneId: tz,
          isFinished: false,
          startDate,
          endDate,
          ...(selection.format?.id ? { eventTypeId: [selection.format.id] } : {}),
          ...(cityLat && cityLng
            ? { latitude: cityLat, longitude: cityLng, distanceInMeters: 30000 }
            : { showAll: true }),
        },
      }).unwrap()
      setEvents((data.content || []).map((e: any) => ({
        eventId: e.eventId,
        eventName: e.eventName,
        city: e.eventAddress?.city,
        startTime: e.startTime,
        avatarUrl: e.avatarUrl,
        organizerUsername: e.organizerUsername,
        description: e.description,
      })))
    } catch (e: any) {
      setError(e?.message || "Не удалось загрузить события")
    } finally {
      setLoading(false)
    }
  }

  const stepTitles: Record<Step, string> = {
    city: "В каком городе?",
    when: "Когда идёшь?",
    format: "Что тебя интересует?",
    tags: "Твои интересы",
    result: "Найденные события",
  }

  const STEPS: Step[] = ["city", "when", "format", "tags"]
  const stepIdx = STEPS.indexOf(step)

  return (
    <>
      <button className={styles.fab} onClick={() => setOpen(true)}>
        <span className={styles.fabIcon}>🎯</span>
        <span className={styles.fabText}>Куда пойти?</span>
      </button>

      {open && (
        <div className={styles.overlay} onClick={close}>
          <div className={styles.dialog} onClick={e => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <div className={styles.dialogTitle}>
                {selection.city && <span className={styles.cityBadge}>{selection.city.emoji} {selection.city.id === "other" ? otherCity || "Другой город" : selection.city.label}</span>}
              </div>
              <button className={styles.closeBtn} onClick={close}>✕</button>
            </div>

            <h2 className={styles.stepTitle}>{stepTitles[step]}</h2>

            {/* Шаг 1 — Город */}
            {step === "city" && (
              <div className={styles.optionsGrid}>
                {CITIES.map(c => (
                  <button key={c.id} className={styles.optionBtn} onClick={() => pickCity(c)}>
                    <span className={styles.optionEmoji}>{c.emoji}</span>
                    <span>{c.label}</span>
                  </button>
                ))}
                <input
                  className={styles.cityInput}
                  placeholder="Введи город..."
                  value={otherCity}
                  onChange={e => setOtherCity(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && otherCity.trim()) {
                      pickCity({ id: "other", label: otherCity, tz: "Europe/Moscow", emoji: "📍", lat: undefined as number | undefined, lng: undefined as number | undefined })
                    }
                  }}
                />
              </div>
            )}

            {/* Шаг 2 — Когда */}
            {step === "when" && (
              <div className={styles.optionsGrid}>
                {WHEN_OPTIONS.map(o => (
                  <button key={o.id} className={styles.optionBtn} onClick={() => pickWhen(o)}>
                    <span className={styles.optionEmoji}>{o.emoji}</span>
                    <span>{o.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Шаг 3 — Формат */}
            {step === "format" && (
              <div className={styles.optionsGrid}>
                {FORMAT_OPTIONS.map((o, i) => (
                  <button key={i} className={styles.optionBtn} onClick={() => pickFormat(o)}>
                    <span className={styles.optionEmoji}>{o.emoji}</span>
                    <span>{o.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Шаг 4 — Теги */}
            {step === "tags" && (
              <>
                <div className={styles.tagsGrid}>
                  {INTEREST_TAGS.map(tag => (
                    <button
                      key={tag}
                      className={`${styles.tag} ${selection.tags.includes(tag) ? styles.tagActive : ""}`}
                      onClick={() => toggleTag(tag)}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                <button className={styles.continueBtn} onClick={doSearch}>
                  🔍 Найти события
                </button>
              </>
            )}

            {/* Результаты */}
            {step === "result" && (
              <div className={styles.result}>
                {/* Сводка выбора */}
                <div className={styles.resultSummary}>
                  {selection.city && <span>{selection.city.emoji} {selection.city.label}</span>}
                  {selection.when && <span>{selection.when.emoji} {selection.when.label}</span>}
                  {selection.format && <span>{selection.format.emoji} {selection.format.label}</span>}
                  {selection.tags.map(t => <span key={t}>#{t}</span>)}
                </div>

                {loading && (
                  <div className={styles.loading}>
                    <div className={styles.spinner} />
                    <p>Ищем события...</p>
                  </div>
                )}

                {error && <p className={styles.errorText}>⚠️ {error}</p>}

                {!loading && !error && events.length === 0 && (
                  <div className={styles.emptyState}>
                    <span className={styles.emptyIcon}>🔍</span>
                    <p>Не нашли подходящих событий. Попробуй другие параметры.</p>
                  </div>
                )}

                {!loading && events.length > 0 && (
                  <div className={styles.eventsList}>
                    <p className={styles.eventsCount}>Найдено: {events.length}</p>
                    {events.slice(0, 8).map(ev => (
                      <div
                        key={ev.eventId}
                        className={styles.eventCard}
                        role="link"
                        tabIndex={0}
                        onClick={() => { setOpen(false); navigate(`/fid/${ev.eventId}`) }}
                        onKeyDown={e => { if (e.key === "Enter") { setOpen(false); navigate(`/fid/${ev.eventId}`) } }}
                      >
                        {ev.avatarUrl?.[0] && (
                          <img
                            src={ev.avatarUrl[0]}
                            alt={ev.eventName}
                            className={styles.eventImg}
                          />
                        )}
                        <div className={styles.eventInfo}>
                          <p className={styles.eventName}>{ev.eventName}</p>
                          <p className={styles.eventMeta}>
                            {ev.city && <span>📍 {ev.city}</span>}
                            {ev.startTime && <span>🕐 {formatDate(ev.startTime)}</span>}
                          </p>
                          {ev.description && (
                            <p className={styles.eventDesc}>{ev.description.slice(0, 80)}...</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className={styles.resultActions}>
                  <button className={styles.resetBtn} onClick={reset}>← Изменить параметры</button>
                </div>
              </div>
            )}

            {/* Прогресс */}
            {step !== "result" && (
              <div className={styles.progress}>
                {STEPS.map((s, i) => (
                  <div key={s} className={`${styles.progressDot} ${i <= stepIdx ? styles.progressDotActive : ""}`} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
